import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Button from '../../../GbsUI/UI/Button';
import Checkbox from '../../../GbsUI/UI/Checkbox';
import ArrowLeftIcon from '../../../GbsUI/UI/icons/ArrowLeft';
import ArrowRightIcon from '../../../GbsUI/UI/icons/ArrowRight';
import Repeater from '../../../GbsUI/UI/Repeater';
import { useTaxonomy } from '../../../hooks/useTaxonomy';
import ExperienceItem from '../components/ExperienceItem';
import SignupLayout from '../components/Layout';
import { SignupContext } from '../context';
import { stepFourSchema, stepFourSchemaWithGroup } from '../schema/step-four';

const SignupStepThree = ({ history }) => {
  const { state, setState } = useContext(SignupContext);
  const [formErrors, setFormErrors] = useState(new Map());
  const initialized = useRef(false);
  const { data: titles, isLoading: titlesLoading } = useTaxonomy('TITLES');
  const { data: roles, isLoading: rolesLoading } = useTaxonomy('ROLES');
  const { data: industries, isLoading: industriesLoading } = useTaxonomy(
    'INDUSTRIES'
  );
  const selectedRole = useMemo(() => state?.experience?.role?.id, [
    state?.experience?.role,
  ]);
  const { data: groups, isLoading: groupsLoading } = useTaxonomy('GROUPS', {
    role: selectedRole,
  });

  const items = useMemo(() => {
    return state.experience.items?.map((item, index) => ({
      removable: index > 0,
      data: item,
    })) || [{
      data: {},
      removable: false,
    }];
  }, [state.experience]);

  const validate = useCallback(
    (data, abortEarly = false, scrollToError = true) => {
      data.items = data.items || [{
        data: {},
        removable: false,
      }];
      const schema =
        (data.flow === 'experienced-professional' || data.acceptedFullTime) && groups.length > 0
          ? stepFourSchemaWithGroup
          : stepFourSchema;

      return schema
        .validate(data, { abortEarly })
        .then(data => {
          setFormErrors(new Map());
          return data;
        })
        .catch(errors => {
          if (errors.inner.length > 0) {
            const errorsMap = errors.inner.reduce((accum, item, index) => {
              const field = document.querySelector(`[name="${item.path}"]`);

              if (field && scrollToError) {
                const details = field.closest('details');

                if (details) {
                  details.open = true;
                }

                if (index === 0) {
                  field.closest('.signup-form-item').scrollIntoView({behavior: 'smooth'});
                }
              }

              accum.set(item.path, item.errors);
              return accum;
            }, new Map());

            setFormErrors(errorsMap);
          } else {
            const errorsMap = new Map();
            errorsMap.set(errors.path, errors.errors);

            setFormErrors(errorsMap);
          }

          return false;
        });
    },
    [groups.length]
  );

  const onFlowChange = useCallback(
    (flow, checked) => {
      setState(prevState => ({
        ...prevState,
        experience: {
          ...prevState.experience,
          flow: checked ? flow : null,
          acceptedFullTime: false,
        },
      }));
    },
    [setState]
  );

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      validate(state.experience).then(valid => {
        if (valid) {
          setFormErrors(new Map());
          history.push('/signup/step/4');
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      });
    },
    [history, state.experience, validate]
  );

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    validate(state.experience, true, false);
  }, [state, validate]);

  return (
    <form onSubmit={onSubmit}>
      <SignupLayout
        title={'Step 3: Work Experience'}
        footer={
          <>
            <Button
              type={'button'}
              variant={'secondary'}
              size={'xl'}
              icon={ArrowLeftIcon}
              iconProps={{ size: 21 }}
              iconPosition={'left'}
              onClick={() => history.push('/signup/step/2')}
            >
              Back
            </Button>
            <Button
              type={'submit'}
              size={'xl'}
              icon={ArrowRightIcon}
              iconProps={{ size: 21 }}
              iconPosition={'right'}
            >
              Continue
            </Button>
          </>
        }
      >
        <div className={'signup-form-row'}>
          <div className={'signup-form-item signup-form-item--full'}>
            <Checkbox
              checked={state?.experience?.flow === 'student'}
              label={
                <span>
                  <strong>Full-Time Student</strong><br />
                  Students enrolled full-time, including those interning or working part-time during their studies.
                </span>
              }
              onChange={e => {
                onFlowChange('student', e.target.checked);
              }}
            />
          </div>
          <div className={'signup-form-item signup-form-item--full'}>
            <Checkbox
              checked={state?.experience?.flow === 'experienced-professional'}
              label={
                <span>
                  <strong>Full-Time Professional</strong><br />
                  Professionals currently working full-time, including those who are part-time students.
                </span>
              }
              onChange={e => {
                onFlowChange('experienced-professional', e.target.checked);
              }}
            />
          </div>
          <div className={'signup-form-item signup-form-item--full'}>
            <Checkbox
              checked={state?.experience?.flow === 'other'}
              label={
                <span>
                  <strong>All Others</strong><br />
                  Job seekers, recent graduates, incoming banking analysts, career switchers, and others in transition.
                </span>
              }
              onChange={e => {
                onFlowChange('other', e.target.checked);
              }}
            />
          </div>
          <div className={'signup-form-item signup-form-item--full'} />
          {!state?.experience?.flow && (
            <div className={'signup-form-item signup-form-item--full'}>
              <Checkbox
                checked={!!state?.experience?.acceptedFullTime}
                label={'Have you accepted an upcoming Full-Time job?'}
                onChange={e => {
                  const acceptedFullTime = e.target.checked;

                  setState(prevState => ({
                    ...prevState,
                    experience: {
                      ...prevState.experience,
                      acceptedFullTime,
                    },
                  }));
                }}
              />
            </div>
          )}
          {(state?.experience?.flow === 'experienced-professional' ||
            state?.experience?.acceptedFullTime) && (
            <div className={'signup-form-item signup-form-item--full'}>
              <Repeater
                items={items}
                addNewText={'Add Additional Work Experience'}
                renderItem={(item, index) => (
                  <ExperienceItem
                    index={index}
                    experience={item.data}
                    titles={titles}
                    roles={roles}
                    groups={groups}
                    industries={industries}
                    errors={formErrors}
                    titlesLoading={titlesLoading}
                    rolesLoading={rolesLoading}
                    groupsLoading={groupsLoading}
                    industriesLoading={industriesLoading}
                  />
                )}
                renderHeader={(item, index) => (
                  <>
                    {item.data.firm ? (
                      <span className={'education-item-header'}>
                        {!!item.data.firm.logo && (
                          <img
                            width={22}
                            height={22}
                            src={item.data.firm.logo}
                            alt={item.data.firm.name}
                          />
                        )}
                        {item.data.firm.name}
                      </span>
                    ) : (
                      <span>Firm {index + 1}</span>
                    )}
                  </>
                )}
                onDelete={index => {
                  setState({
                    ...state,
                    experience: {
                      ...state.experience,
                      items: state.experience.items.filter((_, i) => i !== index),
                    },
                  });
                }}
                onAdd={() => {
                  setState({
                    ...state,
                    experience: {
                      ...state.experience,
                      items: [...state.experience.items, {}],
                    },
                  });
                }}
              />
            </div>
          )}
        </div>
      </SignupLayout>
    </form>
  );
};

export default SignupStepThree;

//
